import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Send, RefreshCw, AlertTriangle } from "lucide-react";
import { api, euro, data, errMsg, scarica } from "@/lib/api";
import { Field, Spinner } from "@/components/Layout";

const CAMPI = [["denominazione", "Ragione sociale / Denominazione"], ["piva", "Partita IVA"], ["codice_fiscale", "Codice fiscale"],
  ["indirizzo", "Indirizzo"], ["cap", "CAP"], ["comune", "Comune"], ["provincia", "Provincia (sigla)"]];
const STATI = { inviata: "badge-ok", errore: "badge-err", in_attesa_configurazione: "badge-warn", da_inviare: "badge-muted" };

const Emittente = ({ conf, reload }) => {
  const [f, setF] = useState(Object.fromEntries(CAMPI.map(([k]) => [k, conf.cedente[k] || ""])));
  const salva = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/fatturazione", f);
      toast.success("Dati emittente salvati");
      reload();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <form onSubmit={salva} className="card grid sm:grid-cols-2 gap-4" data-testid="emittente-form">
      <div className="sm:col-span-2">
        <h3 className="font-display text-2xl">Dati fiscali dell'emittente</h3>
        <p className="text-sm text-[#786E65]">Regime {conf.regime}: fatture senza IVA (natura N2.2) con bollo virtuale da €2 oltre €77,47. Ambiente Aruba: <strong>{conf.ambiente}</strong>.</p>
      </div>
      {CAMPI.map(([k, l]) => (
        <Field key={k} label={l}><input required className="field" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`emittente-${k}-input`} /></Field>
      ))}
      <div className="sm:col-span-2"><button className="btn-primary" data-testid="emittente-salva-btn">Salva</button></div>
    </form>
  );
};

export default function Fatturazione() {
  const [conf, setConf] = useState(null);
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/fatturazione").then((r) => setConf(r.data));
    api.get("/admin/fatture").then((r) => setRows(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (!conf || !rows) return <Spinner />;
  const invia = async (id) => {
    try {
      const { data: d } = await api.post(`/admin/fatture/${id}/invia`);
      d.stato === "inviata" ? toast.success("Fattura inviata ad Aruba") : toast.error(d.errore || d.stato);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const stato = async (id) => {
    try {
      const { data: d } = await api.post(`/admin/fatture/${id}/stato`);
      toast(`Stato SDI: ${d.stato_sdi || "non ancora disponibile"}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6">
      {!conf.aruba_configurato && (
        <div className="flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="aruba-non-configurato">
          <AlertTriangle size={18} className="shrink-0" />Credenziali Aruba non ancora configurate: le fatture vengono numerate e restano in attesa, partono in automatico appena configurate.
        </div>
      )}
      <Emittente conf={conf} reload={load} />
      <div className="card overflow-x-auto">
        <table className="tbl" data-testid="admin-fatture-table">
          <thead><tr><th>Numero</th><th>Data</th><th>Operatore</th><th>Importo</th><th>Bollo</th><th>Stato</th><th>SDI</th><th></th></tr></thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id} data-testid={`fattura-${f.id}`}>
                <td>{f.numero}</td><td>{data(f.data)}</td><td>{f.operatore}</td><td>{euro(f.importo_cent)}</td><td>{f.bollo ? "€ 2,00" : "—"}</td>
                <td><span className={STATI[f.stato]}>{f.stato.replace(/_/g, " ")}</span>{f.errore && <p className="text-xs text-red-700 mt-1 max-w-xs">{f.errore}</p>}</td>
                <td>{f.stato_sdi || "—"}</td>
                <td className="flex gap-1">
                  <button className="btn-secondary btn-sm" title="Scarica XML" data-testid={`fattura-xml-${f.id}`} onClick={() => scarica(`/admin/fatture/${f.id}/xml`, `fattura-${f.numero.replace("/", "-")}.xml`).catch(() => toast.error("Inserisci prima i dati dell'emittente"))}><Download size={14} /></button>
                  {f.stato !== "inviata" ? (
                    <button className="btn-secondary btn-sm" title="Invia" data-testid={`fattura-invia-${f.id}`} onClick={() => invia(f.id)}><Send size={14} /></button>
                  ) : (
                    <button className="btn-secondary btn-sm" title="Aggiorna stato SDI" data-testid={`fattura-stato-${f.id}`} onClick={() => stato(f.id)}><RefreshCw size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="text-center text-[#786E65]">Nessuna fattura emessa</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
